import { useEffect, useState } from 'react'
import { api, type Abweichung, type Auswertung, type QuotenTag, type QuoteBeschreibung } from '../api'
import { anzahl, datumKurz, datumMittel, euro, prozent } from '../format'
import { Karte } from '../komponenten/Karte'
import { Kennzahl } from '../komponenten/Kennzahl'
import { MehrfachLinie, type Reihe } from '../komponenten/MehrfachLinie'
import { Auswertungsraster } from '../komponenten/Auswertungsraster'

/** Wie die drei Sichten einer Quote in der Zeitreihe heißen. */
const SICHTEN = [
  { suffix: '',             name: 'je Tag',            duenn: true },
  { suffix: '_7t',          name: 'gleitend 7 Tage',   duenn: false },
  { suffix: '_auflaufend',  name: 'auflaufend im Monat', gestrichelt: true },
] as const

const KENNZAHL_NAME: Record<string, string> = {
  stornoquote: 'Stornoquote',
  rabattquote: 'Rabattquote',
  trinkgeldquote: 'Trinkgeldquote',
  barquote: 'Barquote',
  sofort_je_100: 'Sofortstornos je 100 Rechnungen',
  durchschnittsbon: 'Durchschnittsrechnung',
}

const KENNZAHL_EINHEIT: Record<string, string> = {
  stornoquote: '%', rabattquote: '%', trinkgeldquote: '%', barquote: '%',
  sofort_je_100: 'je 100 Rechn.',
  durchschnittsbon: '€',
}

const befundWert = (kennzahl: string, wert: string) => {
  const zahl = new Intl.NumberFormat('de-DE',
    { maximumFractionDigits: 2 }).format(Number(wert))
  const einheit = KENNZAHL_EINHEIT[kennzahl]
  return einheit ? `${zahl} ${einheit}` : zahl
}

export function Quoten({ von, bis, betriebe }: { von: string; bis: string; betriebe: string[] }) {
  const [reihe, setReihe] = useState<QuotenTag[]>([])
  const [katalog, setKatalog] = useState<QuoteBeschreibung[]>([])
  const [befunde, setBefunde] = useState<Abweichung[]>([])
  const [kacheln, setKacheln] = useState<Auswertung[]>([])
  const [gewaehlt, setGewaehlt] = useState('stornoquote')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(true)

  useEffect(() => {
    let weg = false
    setLaedt(true); setFehler(null)
    api.auswertungen('quoten').then(async (liste) => {
        const aktiv = liste.filter((k) => k.aktiv).map((k) => k.schluessel)
        const q = await api.quoten(von, bis, betriebe, aktiv)
        if (weg) return
        setKacheln(liste); setReihe(q.reihe ?? []); setKatalog(q.katalog ?? [])
        setBefunde(q.befunde ?? [])
      })
      .catch((e) => { if (!weg) setFehler(e.message) })
      .finally(() => { if (!weg) setLaedt(false) })
    return () => { weg = true }
  }, [von, bis, betriebe.join(',')])

  if (fehler) return <div className="fehler">{fehler}</div>
  if (laedt && !reihe.length) return <div className="laedt">Wird geladen …</div>
  const q = katalog.find((k) => k.schluessel === gewaehlt) ?? katalog[0]
  const istProzent = q?.einheit === '%'
  const wert = (t: QuotenTag, suffix: string) =>
    (t as unknown as Record<string, number | null>)[gewaehlt + suffix] ?? null

  const reihen: Reihe[] = SICHTEN
    .filter((s) => reihe.some((t) => wert(t, s.suffix) !== null))
    .map((s) => ({
      schluessel: s.suffix || 'tag',
      name: s.name,
      werte: reihe.map((t) => wert(t, s.suffix)),
      duenn: 'duenn' in s ? s.duenn : false,
      gestrichelt: 'gestrichelt' in s ? s.gestrichelt : false,
    }))

  const letzter = reihe[reihe.length - 1]
  const f = (v: number | null | undefined) =>
    v === null || v === undefined ? '–'
      : istProzent ? prozent(v, 2)
      : new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(v)

  // Der Monatswert ist der auflaufende des letzten Tages — genau die Zahl,
  // die am Monatsende in der Auswertung stehen wird.
  const heute = letzter ? wert(letzter, '') : null
  const glatt = letzter ? wert(letzter, '_7t') : null
  const auflaufend = letzter ? wert(letzter, '_auflaufend') : null
  const werte = reihe.map((t) => wert(t, '')).filter((v): v is number => v !== null)
  const mittel = werte.length ? werte.reduce((a, b) => a + b, 0) / werte.length : null

  const inhalt = {
    'quoten.verlauf': () => (
      <Karte
        titel={q?.name ?? 'Quote'}
        neben={`${reihe.length} Geschäftstage`}
        werkzeug={
          <div className="knopfleiste" role="group" aria-label="Kennzahl">
            {katalog.map((k) => (
              <button key={k.schluessel} aria-pressed={gewaehlt === k.schluessel}
                      onClick={() => setGewaehlt(k.schluessel)}>
                {k.name.length > 22 ? 'Sofortstornos' : k.name}
              </button>
            ))}
          </div>
        }>
        <div className="gitter g-4" style={{ marginBottom: 16 }}>
          <Kennzahl etikett="Letzter Tag" wert={f(heute)}
                    fuss={letzter ? datumMittel(letzter.tag) : 'keine Geschäftstage'} />
          <Kennzahl etikett="Gleitend 7 Tage" wert={f(glatt)}
                    fuss="Wochenrhythmus herausgerechnet" />
          <Kennzahl etikett="Auflaufend im Monat" wert={f(auflaufend)}
                    fuss="steht so am Monatsende da" />
          <Kennzahl etikett="Mittel im Zeitraum" wert={f(mittel)}
                    fuss={`über ${reihe.length} Tage`} />
        </div>

        {reihe.length ? (
          <MehrfachLinie
            etiketten={reihe.map((t) => datumKurz(t.tag))}
            reihen={reihen}
            formatiere={(v) => f(v)}
            formatiereAchse={(v) => istProzent
              ? new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(v) + ' %'
              : new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(v)}
          />
        ) : <div className="leer">Für diesen Zeitraum liegen keine verdichteten Tage vor.</div>}

        {q && (
          <p style={{
            margin: '16px 0 0', fontSize: 12, color: 'var(--schrift-2)',
            lineHeight: 1.6, maxWidth: '72ch',
          }}>
            {q.erklaerung}
          </p>
        )}
      </Karte>
    ),
    'quoten.tage': () => (
        <Karte titel="Die Tage im Einzelnen" flach>
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Tag</th>
                  <th className="r">Umsatz</th>
                  <th className="r">Storno %</th>
                  <th className="r">Rabatt %</th>
                  <th className="r">Trinkgeld %</th>
                  <th className="r">Bar %</th>
                  <th className="r">Sofortst. je 100</th>
                </tr>
              </thead>
              <tbody>
                {[...reihe].reverse().map((t) => (
                  <tr key={t.tag}>
                    <td>{datumMittel(t.tag)}</td>
                    <td className="r zahl">{euro(t.umsatz)}</td>
                    <td className="r zahl">{prozent(t.stornoquote, 2)}</td>
                    <td className="r zahl">{prozent(t.rabattquote, 2)}</td>
                    <td className="r zahl">{prozent(t.trinkgeldquote, 2)}</td>
                    <td className="r zahl">{prozent(t.barquote, 1)}</td>
                    <td className="r zahl">
                      {t.sofort_je_100 === null ? '–'
                        : new Intl.NumberFormat('de-DE',
                            { maximumFractionDigits: 2 }).format(t.sofort_je_100)}
                      <span className="dezent" style={{ marginLeft: 6, fontSize: 11 }}>
                        ({anzahl(t.sofort_anzahl)})
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Karte>
    ),
    'quoten.abweichungen': () => (
        <Karte titel="Aus dem Rahmen gefallen"
               neben={datumMittel(letzter?.tag ?? bis)}>
          {befunde.length === 0 ? (
            <div className="leer">
              An diesem Tag verhält sich jeder Standort wie sonst auch.
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Standort</th><th>Kennzahl</th>
                  <th className="r">Wert</th><th className="r">sonst</th>
                </tr>
              </thead>
              <tbody>
                {befunde.map((b, i) => (
                  <tr key={i}>
                    <td>{b.betrieb}</td>
                    <td className="dezent">
                      {KENNZAHL_NAME[b.kennzahl] ?? b.kennzahl}
                    </td>
                    <td className="r zahl">
                      <span aria-hidden="true" style={{ marginRight: 4 }}>
                        {b.richtung === 'hoch' ? '▲' : '▼'}
                      </span>
                      {befundWert(b.kennzahl, b.wert)}
                    </td>
                    <td className="r zahl dezent">
                      {befundWert(b.kennzahl, b.normal)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p style={{
            margin: '14px 0 0', fontSize: 11, color: 'var(--schrift-3)',
            lineHeight: 1.6,
          }}>
            Ein Hinweis, dass dieser Tag dem Standort unähnlich sieht — kein
            Verdacht. Verglichen wird jeder Standort mit sich selbst über die
            letzten vier Wochen, und der Tag selbst bleibt aus seinem eigenen
            Vergleich heraus. Ein Standort mit dauerhaft hoher Quote erscheint
            hier nicht; einer mit plötzlich erhöhter schon.
          </p>
        </Karte>
    ),
  }

  return (
    <Auswertungsraster kacheln={kacheln} inhalt={inhalt} />
  )
}
